import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import ChosenBehavior from "../../../behaviors/chosen";
import FilteredModel from "../../../models/chart-reviews/bulk-edit-filtered";
import SelectedModel from "../../../models/chart-reviews/bulk-edit-selected";
import templates from "../../../utils/templates";
import { errorParser, numberFormatter } from "../../../utils";
import confirmationAlert from "../../../utils/alerts/confirmation";

const channel = Radio.channel("global");

const DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_bulk_edit_form"),
};

class View extends Marionette.ItemView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [{ behaviorClass: ChosenBehavior }];
  }

  ui() {
    return {
      chartStatus: "#chartStatus",
      projectId: "#projectId",
      retrievalStatus: "#retrievalStatus",
      reviewersList: "#reviewers",
      keepExistingReviewersCheckbox: "#keepExistingReviewers",
      resetBtn: "button[type=reset]",
      submitBtn: "button[type=submit]",
      savingBtn: "button[type=button]",
    };
  }

  events() {
    return {
      "change @ui.chartStatus": "_onChartStatusChange",
      "change @ui.projectId": "_onProjectChange",
      "change @ui.retrievalStatus": "_onRetrievalStatusChange",
      "change @ui.reviewersList": "_onReviewersListChange",
      reset: "_onReset",
      submit: "_onSubmit",
    };
  }

  initialize() {
    this.payload = this.options.payload;
    this.emptyFormModel = this.model.toJSON();

    // make sure to render the view when the model changes
    this.listenTo(this.model, "change", this._onModelChange);
  }

  onAttach() {
    this._notifyBehaviors();
  }

  _onModelChange() {
    this.render();
    this._notifyBehaviors();
  }

  _notifyBehaviors() {
    this.triggerMethod("ContentRendered");
  }

  _onChartStatusChange() {
    const $selected = this.ui.chartStatus.find(":selected");
    if ($selected.length === 1) {
      const status = $selected.get(0).value;
      this._updateChartStatus(status);
    }
  }

  // marks selected chart status on model
  _updateChartStatus(chartStatus) {
    // grab copy of collection
    const coll = this.model.getStatusList();

    // uncheck all statuses
    coll.each((model) => model.setChecked(false));

    // check selected status
    const status = coll.get(chartStatus);
    status && status.setChecked(true);

    // update model
    this.model.set({
      // chartStatus,
      statusList: coll.toJSON(),
    });
  }

  _onProjectChange() {
    const $selected = this.ui.projectId.find(":selected");
    if ($selected.length === 1) {
      const project = $selected.get(0).value;
      this._updateSelectedProject(project);
    }
  }

  // marks selected project on model
  _updateSelectedProject(projectId) {
    // grab copy of collection
    const coll = this.model.getProjectIdList();

    // uncheck all projects
    coll.each((model) => model.setChecked(false));

    // check selected project
    const project = coll.get(projectId);
    project && project.setChecked(true);

    // update model
    this.model.set({
      projectId,
    });
  }

  _onRetrievalStatusChange() {
    const $selected = this.ui.retrievalStatus.find(":selected");
    if ($selected.length === 1) {
      const status = $selected.get(0).value;
      this._updateSelectedRetrievalStatus(status);
    }
  }

  // marks selected retrieval status on model
  _updateSelectedRetrievalStatus(retrievalStatus) {
    // grab copy of collection
    const coll = this.model.getRetrievalStatusList();

    // uncheck all statuses
    coll.each((model) => model.setChecked(false));

    // check selected status
    const status = coll.get(retrievalStatus);
    status && status.setChecked(true);

    // update model
    this.model.set({
      retrievalStatus,
      retrievalStatusList: coll.toJSON(),
    });
  }

  _onReviewersListChange() {
    const selected = this.ui.reviewersList.find(":selected").length;
    const disableCheckbox = selected ? false : true;

    if (selected) {
      this.ui.keepExistingReviewersCheckbox.parent().removeClass("text-muted");
    } else {
      this.ui.keepExistingReviewersCheckbox.prop("checked", false);
      this.ui.keepExistingReviewersCheckbox.parent().addClass("text-muted");
    }

    this.ui.keepExistingReviewersCheckbox.attr("disabled", disableCheckbox);
  }

  // overrides the default reset behavior. it resets the model instead of the
  // markup.
  _onReset(e) {
    e.preventDefault();

    // reset model with setup data
    this.model.set(this.emptyFormModel);
  }

  _onSubmit(e) {
    e.preventDefault();

    // extract submitted form data
    const data = Backbone.Syphon.serialize(this);

    const payload = Object.assign(
      {},
      { filters: (this.payload && this.payload.filters) || {} },
      { data }
    );

    // add IDs when they exist (selected rows)
    if (this.payload && this.payload.ids) {
      payload["ids"] = this.payload.ids;
    }

    // create API model
    let model = null;
    if (payload.ids) {
      model = new SelectedModel(payload);
    } else {
      model = new FilteredModel(payload);
    }

    // validate it
    if (model.isValid()) {
      this._disableFormButtons();

      // display confirmation
      const title = this._getConfirmationMessage();
      confirmationAlert({ title }).then((result) => {
        if (result.value) {
          // submit on acceptance
          this._saveModel(model);
        } else {
          this._enableFormButtons();
        }
      });
    } else {
      this._enableFormButtons();

      // show warning message
      channel.trigger("status:show", {
        type: "warning",
        message: model.validationError,
      });
    }
  }

  _getConfirmationMessage() {
    const plural = this.model.getChartCount() > 1 ? "s" : "";
    const message = `You have chosen to modify ${numberFormatter(
      this.model.getChartCount()
    )} chart${plural}.`;

    return message;
  }

  _saveModel(model) {
    $.ajax({
      url: model.urlRoot,
      method: "POST",
      contentType: "application/json",
      processData: false,
      data: JSON.stringify(model.toJSON()),
      success: (response, textStatus, jqXHR) => {
        switch (jqXHR.status) {
          case 204:
            // display success alert
            this._updateStatus(
              "success",
              "Your charts were updated successfully."
            );

            // notify parent layout of this change
            this.triggerMethod("dismiss:modal");

            break;
          case 200:
            // display warning alert
            this._updateStatus("danger", response.error["error-message"]);

            this._enableFormButtons();

            break;
        }
      },
      error: (jqXHR) => {
        // display error alert
        this._updateStatus("danger", errorParser(jqXHR));

        this._enableFormButtons();
      },
    });
  }

  _disableFormButtons() {
    this.ui.resetBtn.css("display", "none");
    this.ui.submitBtn.css("display", "none");
    this.ui.savingBtn.removeClass("hidden");
  }

  _enableFormButtons() {
    this.ui.resetBtn.css("display", "inline-block");
    this.ui.submitBtn.css("display", "inline-block");
    this.ui.savingBtn.addClass("hidden");
  }

  _updateStatus(type, message) {
    channel.trigger("status:show", {
      type,
      message,
      autoDismiss: "danger" === type ? false : true,
    });
  }
}

export { View as default };
